import { useNavigate, useParams } from 'react-router';
import {
  Button,
  Footer,
  TitleBar,
  DefaultFactorColumn,
} from '../../../../../components';
import TableV3 from '../../../../../components/generic/Table/TableV3';
import { useLocationName } from '../../../../../hooks';
import { QualitativeFactorStats } from './types';
import { useQualitativeFactorWorkaroundStats } from './hooks';
import { overviewColumns } from '../../../location-applications';
import { tableSortingMethods } from '../../../../../utils';
import { sortQualitativeFactors } from '../../../../../utils/qualitativeFactorUtils';
import { ColumnsType } from 'antd/es/table';

const QualitativeFactorWorkaroundStats = () => {
  const navigate = useNavigate();

  const { locationUUID } = useParams();
  const { locationName } = useLocationName(locationUUID);

  const { stats, statsLoading, statsError } =
    useQualitativeFactorWorkaroundStats(locationUUID);

  // Sort stats by isDefault first, then by name
  const orderedQualitativeFactorsData = stats
    ? sortQualitativeFactors(stats)
    : [];

  const columns: ColumnsType<QualitativeFactorStats> = [
    {
      title: 'Qualitative Factor',
      dataIndex: 'name',
      key: 'name',
      width: 180,
      fixed: 'left',
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: (text: string, record: QualitativeFactorStats) => (
        <DefaultFactorColumn text={text} isDefault={record?.isDefault} />
      ),
    },
    ...overviewColumns,
  ];

  const extendActions = (record: QualitativeFactorStats) => {
    const path = locationUUID
      ? `/cyber/location-level/${locationUUID}/qualitative-factors/workarounds/${record.qualitativeFactorId}`
      : `/cyber/enterprise-level/qualitative-factors/workarounds/${record.qualitativeFactorId}`;
    return (
      <Button
        type="primary-rounded"
        className="w-full text-sm font-semibold min-w-[200px]"
        onClick={() => {
          navigate(path);
        }}
      >
        Review Workarounds
      </Button>
    );
  };

  const title = `${
    locationUUID ? `Location` : 'Enterprise'
  } Qualitative Factor Workarounds`;
  const subtitle = `${
    locationUUID ? `${locationName}` : ''
  } Qualitative Factor Overview`;
  const brief = `
    Below is a list of critical Qualitative Factors essential to business operations.
    These factors may rely on specific IT applications to function effectively.
    Based on the applications mapped to each factor, you can review and document any available workarounds by clicking "Review Workarounds"
  `;

  const backPath = `/cyber/${
    locationUUID ? `location-level/${locationUUID}` : `enterprise-level`
  }/application-workarounds`;
  return (
    <div className="p-2">
      <TitleBar title={title} subtitle={subtitle} brief={brief} />
      <TableV3
        columns={columns}
        loading={statsLoading}
        data={orderedQualitativeFactorsData}
        rowKey="qualitativeFactorId"
        extendActions={extendActions}
        error={statsError}
      />
      <Footer backPath={backPath} />
    </div>
  );
};

export default QualitativeFactorWorkaroundStats;
